import type { ReactNode } from 'react';
import { SidebarProvider, useSidebar } from '@/app/providers/SidebarProvider';
import { Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import AppHeader from './AppHeader';
import Backdrop from './Backdrop';
import AppSidebar from './AppSidebar';
import { PageContainer } from '@/shared/components/PageContainer';
import { usePageLayout, type PageLayout } from './pageLayout';

interface AppLayoutProps {
    children?: React.ReactNode;
}

function pageContent(layout: PageLayout, content: ReactNode) {
    if (layout === 'full-width') return content;
    if (layout === 'fill-height') return <PageContainer fillHeight>{content}</PageContainer>;
    return <PageContainer>{content}</PageContainer>;
}

const CONTENT_MOTION =
    'lg:motion-safe:transition-[margin-left] lg:motion-safe:duration-200 lg:motion-safe:ease-shell';

const LayoutContent: React.FC<AppLayoutProps> = ({ children }) => {
    const { isRail } = useSidebar();
    const pageLayout = usePageLayout();
    const content = children || <Outlet />;

    return (
        <div className="flex h-[calc(100dvh-var(--env-banner-height))] overflow-hidden">
            <div>
                <AppSidebar />
                <Backdrop />
            </div>
            <div
                className={`flex min-w-0 flex-1 flex-col ${CONTENT_MOTION} ${isRail ? 'lg:ml-22.5' : 'lg:ml-72.5'}`}
            >
                <AppHeader />
                {/* relative: absolutely positioned descendants (sr-only labels among them) scroll with main
                    instead of stretching the document. */}
                <main className={`relative min-h-0 flex-1 overflow-y-auto ${pageLayout === 'fill-height' ? 'flex flex-col' : ''}`}>
                    {pageContent(pageLayout, content)}
                </main>
            </div>
            <ToastContainer position="bottom-right" style={{ zIndex: 999999 }} />
        </div>
    );
};

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
    return (
        <SidebarProvider>
            <LayoutContent>{children}</LayoutContent>
        </SidebarProvider>
    );
};
